<template>
    <div>
        <internal-header v-if="!pdf_view" heading="Member Activity Report"/>
        <member-activity-report-filters
            v-if="$vuetify.breakpoint.mdAndUp && !pdf_view"
            v-model="filters"
            :constants="constants"
            :valuelists="valuelists"
        />
        <v-expansion-panels v-else-if="!pdf_view" flat>
            <v-expansion-panel>
                <v-expansion-panel-header hide-actions>
                    <template v-slot:default="{ open }">
                        <v-container class="py-0">
                            <v-row no-gutters>
                                <v-col cols="11" class="tw-text-lg">Filters<span v-show="!!filters_used">: {{ filters_used }}</span></v-col>
                                <v-col cols="1">
                                    <v-icon>
                                        <template v-if="open">mdi-minus-circle</template>
                                        <template v-else>mdi-plus-circle</template>
                                    </v-icon>
                                </v-col>
                            </v-row>
                        </v-container>
                    </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                    <member-activity-report-filters
                        v-model="filters"
                        :constants="constants"
                        :valuelists="valuelists"
                    />
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>

        <div v-if="!pdf_view" class="tw-flex tw-flex-row tw-w-full tw-justify-between tw-items-center tw-pt-2 tw-pb-1 tw-mb-2 tw-border-b tw-border-t tw-border-dark-grey">
            <div v-if="!pdf_view" class="flex-grow-1 tw-text-xl tw-font-light" v-show="num_records_filtered">{{ num_records_filtered }} Records</div>
            <div v-if="!pdf_view" v-show="records_are_present" @click="exportAsPdf()" class="flex-grow-0 tw-px-3 tw-text-black tw-underline tw-text-xs tw-font-normal tw-text-link tw-cursor-pointer">Print List</div>
        </div>
        <!-- Report Header -->
        <div v-if="pdf_view" class="tw-flex tw-items-center tw-flex-col tw-mb-6" id="report-header">
            <div style="font-size: 16px" v-if="filters_prop.date_deposit">{{ showSelectedDepositDate() }}</div>
            <div style="font-size: 16px">{{ showSelectedMemberName(items ?? null) }}</div>
            <div>{{ showSelectedMemberAddress(items ?? null) }}</div>
        </div>

        <smart-table
            ref="member_activity_list"
            endpoint="reports.list.memberactivity"
            :pdf_endpoint="route('reports.memberactivity.queue', 'pdf').url()"
            :items="items"
            :hide-default-header="true"
            :hide-default-footer="pdf_view"
            :headers="headers"
            :search="filters.search"
            group-by="production_id"
            :items-per-page="getDefaultItemsPerPage()"
            :footer-props="{
                'items-per-page-options': getDefaultItemsPerPageOptions()
            }"
            flat
            @fetch-complete="receivedRecordsUpdate"
            :auto_update="!!filters.user_id"
            dense
            id="member-activity-table"
        >
            <template v-slot:header="{props}">
                <tr>
                    <th colspan="2"></th>
                    <th colspan="4" style="border-width: 1px 1px 0 1px">RRSP</th>
                    <th colspan="5" style="border-width: 1px 1px 0 1px">Health & Welfare</th>
                    <th colspan="1" style="border-width: 1px 1px 0 0"></th>
                </tr>
                <tr style="border-width: 0 1px 1px 0">
                    <td>Production</td>
                    <td>Period Ending</td>
                    <td style="border-width: 0 0 0 1px">Employer</td>
                    <td>Voluntary</td>
                    <td>Daily</td>
                    <td style="border-width: 0 1px 0 0">Total</td>
                    <td style="border-width: 0 0 0 1px">Employer</td>
                    <td>Daily</td>
                    <td>Employee</td>
                    <td>Total</td>
                    <td style="border-width: 0 1px 0 0" >Net</td>
                    <td style="border-width: 0 1px 0 1px">Dues</td>
                </tr>
            </template>


            <template v-slot:group="{items, group, options}">
                <!-- Group Header -->
                <tr>
                    <th colspan="2" style="border-width: 0">
                        {{ items[0].production?.name || 'N/A'}}
                    </th>
                    <td colspan="4" style="border-width: 0 0 0 1px"></td>
                    <td colspan="5" style="border-width: 0 1px 0 1px"></td>
                    <td colspan="1" style="border-width: 0 1px 0 1px"></td>
                </tr>

                <!-- Group Data -->
                <tr v-for="item in items" :key="item.id">
                    <td></td>
                    <td style="border-width: 0 1px 0 0">{{item.period_ending}}</td>
                    <td style="border-width: 0 0 1px 1px">{{$root.formatCurrency(item.pension ?? 0)}}</td>
                    <td style="border-width: 0 0 1px 0">{{$root.formatCurrency(item.pension_deducted_employee ?? 0)}}</td>
                    <td style="border-width: 0 0 1px 0">{{$root.formatCurrency(item.rrsp_daily ?? 0)}}</td>
                    <td style="border-width: 0 1px 1px 0">{{$root.formatCurrency(item.pension_deducted_total ?? 0)}}</td>
                    <td style="border-width: 0 0 1px 1px">{{$root.formatCurrency(item.HW_employer ?? 0)}}</td>
                    <td style="border-width: 0 0 1px 0">{{$root.formatCurrency(item.HW_daily ?? 0)}}</td>
                    <td style="border-width: 0 0 1px 0">{{$root.formatCurrency(item.HW_employee ?? 0)}}</td>
                    <td style="border-width: 0 0 1px 0">{{$root.formatCurrency(item.hw_total_calc ?? 0)}}</td>
                    <td style="border-width: 0 1px 1px 0">{{$root.formatCurrency(item.hw_net_calc ?? 0)}}</td>
                    <td style="border-width: 0 1px 1px 0">{{$root.formatCurrency(item.dues_amount ?? 0)}}</td>
                </tr>

                <!-- Sub Total -->
                <tr style="background-color: #F2F2F2; height: 40px">
                    <th colspan="2" style="border-width: 0">Sub Total for {{items[0].production?.name || 'N/A'}}</th>
                    <th style="border-width: 1px 0 1px 1px">{{ $root.formatCurrency(getSumFromItems(items, 'pension')) }}</th>
                    <th style="border-width: 1px 0 1px 0">{{ $root.formatCurrency(getSumFromItems(items, 'pension_deducted_employee')) }}</th>
                    <th style="border-width: 1px 0 1px 0">{{ $root.formatCurrency(getSumFromItems(items, 'rrsp_daily')) }}</th>
                    <th style="border-width: 1px 1px 1px 0">{{ $root.formatCurrency(getSumFromItems(items, 'pension_deducted_total')) }}</th>
                    <th style="border-width: 1px 0 1px 0">{{ $root.formatCurrency(getSumFromItems(items, 'HW_employer')) }}</th>
                    <th style="border-width: 1px 0 1px 0">{{ $root.formatCurrency(getSumFromItems(items, 'HW_daily')) }}</th>
                    <th style="border-width: 1px 0 1px 0">{{ $root.formatCurrency(getSumFromItems(items, 'HW_employee')) }}</th>
                    <th style="border-width: 1px 0 1px 0">{{ $root.formatCurrency(getSumFromItems(items, 'hw_total_calc')) }}</th>
                    <th style="border-width: 1px 1px 1px 0">{{ $root.formatCurrency(getSumFromItems(items, 'hw_net_calc')) }}</th>
                    <th style="border-width: 1px 1px 1px 1px">{{ $root.formatCurrency(getSumFromItems(items, 'dues_amount')) }}</th>
                </tr>
            </template>

            <!-- Grand Total -->
            <template v-slot:body.append="{items, originalItemsLength,pagination,options,groupedItems,headers,isMobile}">
                <tr style="background-color: #E6E6E6; height: 40px">
                    <th colspan="2">Grand Total for {{ showSelectedMemberName(fetch_data_response?.data?.data ?? null) }}</th>
                    <th>{{ $root.formatCurrency(getSumFromItems(items, 'pension')) }}</th>
                    <th>{{ $root.formatCurrency(getSumFromItems(items, 'pension_deducted_employee')) }}</th>
                    <th>{{ $root.formatCurrency(getSumFromItems(items, 'rrsp_daily')) }}</th>
                    <th>{{ $root.formatCurrency(getSumFromItems(items, 'pension_deducted_total')) }}</th>
                    <th>{{ $root.formatCurrency(getSumFromItems(items, 'HW_employer')) }}</th>
                    <th>{{ $root.formatCurrency(getSumFromItems(items, 'HW_daily')) }}</th>
                    <th>{{ $root.formatCurrency(getSumFromItems(items, 'HW_employee')) }}</th>
                    <th>{{ $root.formatCurrency(getSumFromItems(items, 'hw_total_calc')) }}</th>
                    <th>{{ $root.formatCurrency(getSumFromItems(items, 'hw_net_calc')) }}</th>
                    <th>{{ $root.formatCurrency(getSumFromItems(items, 'dues_amount')) }}</th>
                </tr>
            </template>
        </smart-table>

    </div>
</template>

<script>
    import AppLayout from "@/Layouts/AppLayout.vue";
    import InternalHeader from "@/Generic/InternalHeader.vue";
    import MemberActivityReportFilters from "@/Pages/InternalPages/Reports/MemberActivityReportFilters.vue";
    import SmartTable from "@/Generic/SmartTable";

    export default {
        name: "MemberActivityReport",
        layout: (h, page) => {
            return h(AppLayout, [
                page
            ])
        },
        components: {
            InternalHeader,
            MemberActivityReportFilters,
            SmartTable,
        },
        props: {
            'constants': {
                type: Object,
                required: false,
                default: function () {
                    return undefined
                }
            },
            'valuelists': {
                type: Object,
                required: false,
                default: function () {
                    return undefined
                }
            },
            'items': {          // Will optionally accept a hardcoded list of items instead of getting smart table to fetch them
                type: Array,
                required: false,
                default: function () {
                    return undefined
                }
            },
            'filters_prop': {          // Will optionally accept a list of filters used in the request
                type: Array,
                required: false,
                default: function () {
                    return undefined
                }
            },
            'pdf_view': {          // Indicates if this instance is a PDF view
                type: Boolean,
                required: false,
                default: function () {
                    return false
                }
            },
        },
        data() {
            return {
                constant: this.constants,
                filters: {
                    user_id: null,
                    date_deposit: null,
                },
                fetch_data_response: null,
                items_local: this.items || null,
            }
        },
        computed: {
            headers() {
                return [
                    {value: 'period_ending',                text: 'Period Ending',  align: `start`, sortable: false, filterable: false,   },
                    {value: 'pension',                      text: 'Pension',        align: `start`, sortable: false, filterable: false,   },
                    {value: 'pension_deducted_employee',    text: 'Voluntary',      align: `start`, sortable: false, filterable: false,   },
                    {value: 'rrsp_daily',                   text: 'Daily',          align: `start`, sortable: false, filterable: false,   },
                    {value: 'pension_deducted_total',       text: 'Total',          align: `start`, sortable: false, filterable: false,   },
                    {value: 'HW_daily',                     text: 'Employer',       align: `start`, sortable: false, filterable: false,   },
                    {value: 'HW_employer',                  text: 'Daily',          align: `start`, sortable: false, filterable: false,   },
                    {value: 'HW_employee',                  text: 'Employee',       align: `start`, sortable: false, filterable: false,   },
                    {value: 'hw_total_calc',                text: 'Total',          align: `start`, sortable: false, filterable: false,   },
                    {value: 'hw_net_calc',                  text: 'Net',            align: `start`, sortable: false, filterable: false,   },
                    {value: 'dues_amount',                  text: 'Dues',           align: `start`, sortable: false, filterable: false,   },
                    {value: 'id',                           text: 'ID',             align: `start`, sortable: false, filterable: false,   },
                    {value: 'user_id',                      text: 'User',           align: ` d-none`, sortable: false, filterable: true, search: this?.filters?.user_id },
                    {value: 'date_deposit',                 text: 'Deposit Date',   align: ` d-none`, sortable: false, filterable: true, search: this?.filters?.date_deposit},
                ]
            },
            filters_used() {
                let filters_used = Object.keys(this.filters).filter(filter => (!!this.filters[filter] && this.filters[filter].length))
                if (filters_used?.length === 4) return "All"
                if (!filters_used || filters_used.length === 0) return ""
                return filters_used?.join(", ").toProperCase()
            },
            num_records_total() {
                return this.fetch_data_response?.data.recordsTotal
            },
            num_records_filtered() {
                return this.fetch_data_response?.data.recordsFiltered
            },
            records_are_present() {
                return this.fetch_data_response?.data.recordsFiltered
            },
        },
        methods: {
            receivedRecordsUpdate(response) {
                this.fetch_data_response = response
            },
            getDefaultItemsPerPage() {
                return this.pdf_view ? -1 : 100
            },
            getDefaultItemsPerPageOptions() {
                return this.pdf_view ? [-1] : [100, 500, 1000]
            },
            getSumFromItems(items, key){
                let sum = 0
                items.forEach(item => {
                    sum = sum +  Number(item[key] ?? 0)
                })
                return sum.toFixed(2)
            },
            showSelectedMemberName(list) {
                if (list) {
                    return (list[0]?.member?.first_name ?? '') + " " + (list[0]?.member?.last_name ?? '')
                }
                return null
            },
            showSelectedMemberAddress(list) {
                if (list) {
                    return list[0]?.member?.addresses[0]?.single_line_mailing_address
                }
                return null
            },
            showSelectedDepositDate() {
                if (this.filters_prop.date_deposit) {
                    return (this.filters_prop.date_deposit[0] ?? '') + "..." + (this.filters_prop.date_deposit[1] ?? '')
                }
                return null
            },
            async exportAsPdf() {

                let payload = {filter:this.filters}
                let ajax_uri = route('reports.memberactivity.queue', 'pdf').url()
                let ajax_method = 'post'

                axios({
                    url: ajax_uri,
                    method: ajax_method,
                    data: payload,
                })
                    .then(response => {
                        if (response.data.success) {
                            this.$root.snackbar.success(`You will receive an email to ${this.$page.user?.email} with the PDF once it is ready.`)
                        } else {
                            throw (response.data.error)
                        }
                    })
                    .catch((error) => {
                        this.$root.snackbar.error(error.response.data.message)
                    })


            },
        },
        watch: {
            items: {
                handler(newVal, oldVal) {
                    // this.items just got updated
                },
                deep: true,
                immediate: true,
            },
        },
    }
</script>

<style>
#member-activity-table tr,td,th {
    border-color: black !important;
}
#member-activity-table td,th {
    padding: 0 16px;
    border-width: 0;
}
#member-activity-table tr:hover{
    background-color: inherit;
}


@media print {
    .pagebreak {
        page-break-after: always;
        min-height: 1px;
    }

    html, body {
        font-size: 10pt; /* changing to 10pt has no impact */
        margin-top: -40px;
    }

}
</style>
